import { ReactNode, useCallback } from 'react';
// eslint-disable-next-line no-restricted-imports
import { useIntl } from 'react-intl';

export type TranslationKey = string;

type PrimitiveType = string | number | boolean | null | undefined | Date;
type FormatXMLElementFn<T, R = string | Array<string | T>> = (
  parts: Array<string | T>,
) => R;

export interface Translate<T = string, R = T> {
  (
    key: TranslationKey,
    values?: Record<
      string,
      PrimitiveType | ReactNode | FormatXMLElementFn<string, ReactNode>
    >,
    fallback?: string,
  ): string;
  (
    key: TranslationKey,
    values?: Record<
      string,
      PrimitiveType | ReactNode | FormatXMLElementFn<T, R>
    >,
    fallback?: string,
  ): ReactNode;
}

export const useTranslation = (
  id: TranslationKey,
  values?: Record<string, any>,
  fallback?: string,
) => {
  const intl = useIntl();

  if (!intl.messages[id]) {
    return fallback || (id as string);
  }

  return intl.formatMessage({ id, defaultMessage: fallback }, values) as string;
};

export const useTranslate = (): Translate<string> => {
  const intl = useIntl();
  const translate = useCallback(
    (id: TranslationKey, values?: any, fallback?: string) => {
      if (!intl.messages[id]) {
        return fallback || (id as string);
      }

      return intl.formatMessage({ id, defaultMessage: fallback }, values);
    },
    [intl],
  ) as Translate<string>;

  return translate;
};
